import React from 'react';
import cn from 'classnames';

import CompanyLogo from '../../components/companyLogo';
import { kCurrencyFormatter } from '../../utils';
import {
  buildCmpSubpagesUrls,
  buildCppSubpageUrl,
  formatCurrency,
  formatNumber,
} from '../../utils';
import { HASH_SUFFIX } from '../../utils/constant';

import { CHART_TYPES } from './chartUtils';

import classes from './index.module.css';

const SalaryChartItem = ({
  type,
  index,
  minPercentLow,
  maxPercentHigh,
  salaryChartItem,
  isDesktop,
  isHidden = false,
}) => {
  const calculateLeft = (value) => {
    const diff = maxPercentHigh - minPercentLow;
    return ((value - minPercentLow) / diff) * 100;
  };

  const renderSalaryChartTypeA = () => {
    const { title, link, median, onLinkClick, isLinkIndexed, disabled } = salaryChartItem;

    return (
      <>
        <td className={`d-none d-md-block col-md-1 text-center px-0`}>
          <span className={cn('openSansBlackBold', classes['salary-chart-text'])}>{`${index}`}</span>
        </td>
        <td className={cn('col-6', 'col-md-4', 'px-0' ,'w-100', classes['ellipsis'])}>
          {isLinkIndexed ? (
            <a
              href={link}
              className={cn('openSansBlackBold', 'salary-chart-title', classes['salary-chart-link'])}
              onClick={onLinkClick}
              onContextMenu={onLinkClick}
            >
              {`${title}`}
            </a>
          ) : (
            <div className={cn('openSansBlackBold', 'salary-chart-title', classes['salary-chart-text'], classes['ellipsis'])}>
              {`${title}`}
            </div>
          )}
        </td>
        <td className={`col-6 col-md-4 text-center`}>
          <div className={`d-flex flex-column`}>
            <div className={cn('openSansBlackBold', classes['salary-chart-text'])}>{formatCurrency(median)}</div>
          </div>
        </td>
        <td className={cn('d-none', 'd-md-flex', 'flex-column', 'align-items-end', 'col-md-3', 'pr-0', classes['min-salary-range-container'])}>
          <div className={cn(classes['min-salary-range-bar'])}>
            <div
              className={cn(classes['arrow'], {disabled: disabled})}
              style={{ left: `${calculateLeft(median)}%` }}
            />
            <div className={cn(classes['bar'], {disabled: disabled})} />
          </div>
          <div className={cn('d-flex justify-content-between', classes['k-salary'])}>
            <div className="lightTextStyleGrey">{kCurrencyFormatter(minPercentLow, true)}</div>
            <div className="lightTextStyleGrey">{kCurrencyFormatter(maxPercentHigh, true)}</div>
          </div>
        </td>
      </>
    );
  };

  const renderSalaryChartTypeB = () => {
    const {
      companyNameCPS,
      companyLogo,
      companyLink,
      zippiaCppUrl,
      toBeIndexed,
      displayFlagOfCSP,
      median,
      overallScore,
      comboJobTitleLink,
      jobIndexedLink,
      displayFlagOfCompanyJobsPage,
    } = salaryChartItem;

    const imgWidthHeight = isDesktop ? 50 : 50;
    const imgWidth = imgWidthHeight;
    const imgHeight = imgWidthHeight;

    const avgSalary = displayFlagOfCSP ? (
      <a
        href={buildCppSubpageUrl(companyLink || zippiaCppUrl, '', 'salary')}
        className={cn('openSansBlackBold', classes['salary-chart-link'])}
      >
        {formatCurrency(median)}
      </a>
    ) : (
      <span className={cn('openSansBlackBold', classes['salary-chart-text'])}>{formatCurrency(median)}</span>
    );

    const TextOnly = () => (
      <div className={cn('openSansBlackBold', 'w-100', classes['salary-chart-text'])}>
        <span className={classes.rankLabel}>{`${index}.`}</span>
        <span>{`${companyNameCPS}`}</span>
      </div>
    );

    const ComboLink = () => (
      <a
        href={comboJobTitleLink || buildCppSubpageUrl(companyLink || zippiaCppUrl, '', 'jobs')}
        className={cn('openSansBlackBold', 'd-flex', classes['salary-chart-link'], classes['salaryChartLink'])}
      >
        <span className={classes.rankLabel}>{`${index}.`}</span>
        <span className={cn(classes['salary-chart-text'])}>{`${companyNameCPS}`}</span>
      </a>
    );

    const OverviewLink = () => (
      <a
        href={buildCppSubpageUrl(companyLink || zippiaCppUrl, '', 'overview')}
        className={cn('openSansBlackBold', 'd-flex', classes['salary-chart-link'], classes.salaryChartLink)}
      >
        <span className={classes.rankLabel}>{`${index}.`}</span>
        <span className={cn(classes['salary-chart-text'])}>{`${companyNameCPS}`}</span>
      </a>
    );

    return (
      <>
        <td className={`d-none d-md-flex align-items-center col-md-1 px-0`}>
          <span className={cn('openSansBlackBold', classes['salary-chart-text'])}>{`${index}`}</span>
        </td>
        <td className="col-7 col-md-6 pl-0 pl-md-0 pr-0">
          {toBeIndexed ? (
            <div className="d-flex align-items-center">
              <div className="d-none d-md-flex px-1">
                <a href={buildCppSubpageUrl(companyLink || zippiaCppUrl, '', 'overview')}>
                  <CompanyLogo
                    className="CompanyLogoImg"
                    companyLogo={companyLogo}
                    companyName={companyNameCPS}
                    size="small"
                    imgWidth={imgWidth}
                    imgHeight={imgHeight}
                  />
                </a>
              </div>
              <div className={`${classes.nameColumn} line-clamp-2 w-100`}>
                {jobIndexedLink || displayFlagOfCompanyJobsPage ? (
                  <ComboLink />
                ) : (
                  <>{jobIndexedLink === false ? <TextOnly /> : <OverviewLink />}</>
                )}
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center">
              <div className="d-none d-md-flex px-1">
                <CompanyLogo
                  className="CompanyLogoImg"
                  companyLogo={companyLogo}
                  companyName={companyNameCPS}
                  size="small"
                  imgWidth={imgWidth}
                  imgHeight={imgHeight}
                />
              </div>
              <div className={`${classes.nameColumn} line-clamp-2 w-100`}>
                {jobIndexedLink || displayFlagOfCompanyJobsPage ? <ComboLink /> : <TextOnly />}
              </div>
            </div>
          )}
        </td>
        <td className={`d-none d-md-flex align-items-center col-md-2 px-1`}>
          <div className={`d-flex ZippiaTable mt-0`}>
            <div className={cn('openSansBlackBold', classes['salary-chart-text'])}>
              {toBeIndexed ? (
                <a
                  href={`${buildCppSubpageUrl(
                    companyLink || zippiaCppUrl,
                    '',
                    'overview'
                  )}#reviews${HASH_SUFFIX}`}
                >
                  {overallScore}
                </a>
              ) : (
                overallScore
              )}
            </div>
          </div>
        </td>
        <td className="d-flex align-items-center col-5 col-md-3 px-0">{avgSalary}</td>
      </>
    );
  };

  const renderSalaryChartTypeC = () => {
    const { jobCount, jobTitle, median, link, isLink, jobTitleIndexes } = salaryChartItem;

    const isSalaryPageIndexed = jobTitleIndexes['indexInfoOfSalaryPage'] === 'INDEX';
    const isJobsPageIndexed = jobTitleIndexes['indexInfoOfJobsPage'] === 'INDEX';

    const anchorLink = link || buildCmpSubpagesUrls(jobTitle, '', 'salary');
    const isLinkIndexed = link ? isLink : isSalaryPageIndexed;

    return (
      <>
        <td className={cn('col-12', 'col-md-6', 'pl-0', classes['ellipsis'])}>
          {isLinkIndexed ? (
            <a href={anchorLink} className={cn('openSansBlackBold', classes['salary-chart-link'])}>
              {jobTitle}
            </a>
          ) : (
            <span className={cn('openSansBlackBold', classes['salary-chart-text'])}>{jobTitle}</span>
          )}
        </td>
        <td className={cn('col-12', 'col-md-3', 'text-center', 'px-0', classes['company-salary-field'])}>
          <div className={cn('order-last', 'order-md-first', 'openSansBlackBol', classes['salary-chart-text'])}>
            {formatCurrency(median)}
          </div>
        </td>
        <td className={('col-12', 'col-md-3', 'text-center', 'px-0', classes['company-salary-field'])}>
          <div className={`order-last order-md-first`}>
            {isJobsPageIndexed ? (
              <a
                href={buildCmpSubpagesUrls(jobTitle, '', 'jobs')}
                className={cn('openSansBlackBold', classes['salary-chart-link'])}
              >
                {`${formatNumber(jobCount)}+`}
              </a>
            ) : (
              <span className={cn('openSansBlackBold', classes['salary-chart-text'])}>
                {`${formatNumber(jobCount)}+`}
              </span>
            )}
          </div>
        </td>
      </>
    );
  };

  const renderSalaryChartItem = () => {
    switch (type) {
      case CHART_TYPES.TYPE_B:
        return renderSalaryChartTypeB();
      case CHART_TYPES.TYPE_C:
        return renderSalaryChartTypeC();
      default:
        return renderSalaryChartTypeA();
    }
  };

  return (
    <tr
      className={cn(classes['salary-chart-item-container'], 'flex-wrap', {
        [classes['odd']]: index % 2 !== 0,
        'd-none': isHidden,
        'd-flex': !isHidden,
      })}
    >
      {renderSalaryChartItem()}
    </tr>
  );
};

export default SalaryChartItem;
